import { useEffect, useMemo, useRef } from 'react';
import { isPolarisEmbed } from '@/lib/embed-theme';
import { postEmbedMessage } from '@/lib/embed-messages';
import { describeApiError } from '@/lib/api-errors';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import i18n from '@/lib/i18n';
import {
  useAuthStore,
  useDesignStore,
  useProductSelectionStore,
} from '@/stores';
import { awaitPendingSaves } from '@/stores/designStore';
import { usePublishStore } from '@/stores/publishStore';
import { useCanvasStateManager } from '@/stores/canvasStateManager';
import { updateDesign } from '@/api/designs';
import { publishToChannels } from '@/api/publishChannels';
import { isSendableChannel } from '@/lib/sendable-channel';
import { usePublishChannels } from '@/hooks/usePublishChannels';
import { useProducts } from '@/hooks/useProducts';
import { requestCloudCallback } from '@/api/cloudCallback';
import { designKeys } from '@/hooks/useDesigns';
import { isImpersonationTab } from '@/lib/impersonation-session';
import {
  activeCalibrationBlocker,
  positionsWithPlacement,
} from '@/lib/publish-calibration';

/**
 * How a publish from an admin impersonation tab reaches the shop. There is
 * no embedding shop window and no channel in the URL, so the choice rests
 * on data: the marketplace id on the account, else nothing (saved and
 * activated only). A host handover is never offered here.
 */
export type ImpersonationHandover = 'marketplace' | 'none';

/**
 * Up to two product names in full, more as "A, B and N more" — long enough
 * to act on, short enough for one line under the button.
 */
export function listProductNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  if (names.length === 2) {
    return i18n.t('creator.publish.namesTwo', { a: names[0], b: names[1] });
  }
  return i18n.t('creator.publish.namesMore', {
    a: names[0],
    b: names[1],
    count: names.length - 2,
  });
}

/**
 * Shared publish action for Step 3 (save design + activate).
 *
 * `publish()` runs in this order: mark publishing → invalidate the AI
 * suggestion → copy title and description into the design store → wait for
 * a running save and its trailing pass → save once more → adopt ACTIVE in
 * the store without dirtying it → PATCH the status → invalidate the design
 * list → hand over (marketplace, cloud or the overview) → clear the flag.
 * The flag lives in the publish store so the auto-save timer, the step bar
 * and the leave gate can read it; every exit path clears it.
 */
/**
 * After a handover the design's unpublished-changes flag changes on the
 * server; list and detail refetch so the hint follows. The marketplace
 * door freezes in the background, so the flag may trail it briefly.
 */
function invalidateDesign(
  queryClient: ReturnType<typeof useQueryClient>,
  designId: string
) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: designKeys.lists() }),
    queryClient.invalidateQueries({ queryKey: designKeys.detail(designId) }),
  ]);
}

export function usePublishDesign() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refreshDesign = (designId: string) =>
    invalidateDesign(queryClient, designId);

  const totalVariants = useProductSelectionStore((state) =>
    Object.values(state.selections)
      .filter((s) => s.isActive)
      .reduce((sum, s) => sum + s.selectedColors.length, 0)
  );

  // Active products without a selling price cannot publish (the server
  // refuses them with design_product_unpriced); the step says which ones.
  const selections = useProductSelectionStore((state) => state.selections);
  const { data: apiProducts } = useProducts();
  const unpricedProductNames = useMemo(
    () =>
      Object.entries(selections)
        .filter(
          ([, s]) =>
            s.isActive &&
            s.selectedColors.length > 0 &&
            typeof s.sellingPrice !== 'number'
        )
        .map(
          ([productId]) =>
            apiProducts?.find((product) => product.id === productId)?.name ??
            productId
        ),
    [selections, apiProducts]
  );

  const title = usePublishStore((state) => state.title);
  const description = usePublishStore((state) => state.description);
  const tags = usePublishStore((state) => state.tags);
  const category = usePublishStore((state) => state.category);
  const collection = usePublishStore((state) => state.collection);
  const selectedChannelIds = usePublishStore(
    (state) => state.selectedChannelIds
  );
  const setChannels = usePublishStore((state) => state.setChannels);
  const aiGenerationStatus = usePublishStore(
    (state) => state.aiGenerationStatus
  );
  const invalidateAiGeneration = usePublishStore(
    (state) => state.invalidateAiGeneration
  );
  const isPublishing = usePublishStore((state) => state.isPublishing);
  const setIsPublishing = usePublishStore((state) => state.setIsPublishing);

  const saveDesign = useDesignStore((state) => state.saveDesign);
  const adoptStatus = useDesignStore((state) => state.adoptStatus);
  const setDesignName = useDesignStore((state) => state.setDesignName);
  const setDesignDescription = useDesignStore(
    (state) => state.setDesignDescription
  );

  const iframeChannelName = useAuthStore((state) => state.iframeChannelName);
  const storedChannelName = useDesignStore((state) => state.channelName);
  // Optional passthrough: a Phoenix session has no channel name at all.
  const handoverChannelName =
    iframeChannelName ?? storedChannelName ?? undefined;
  const channelPublications = useDesignStore(
    (state) => state.channelPublications
  );
  const publishRunning = useDesignStore((state) => state.publishRunning);

  // Channels the listing already sits on: shown as "already there" and never
  // enqueued again from this step (the overview re-sends failed ones).
  const publishedChannelIds = useMemo(
    () =>
      channelPublications
        .filter((publication) => publication.status === 'PUBLISHED')
        .map((publication) => publication.channelId),
    [channelPublications]
  );

  // The server decides whether this session hands over to the cloud; the web
  // app used to infer it from a URL parameter plus a missing marketplace link,
  // which also mistook an unlinked marketplace seller for a cloud seller.
  const channelResolution = usePublishChannels();
  const isCloudSession = channelResolution.data?.mode === 'cloud';
  const availableChannels = useMemo(
    () => channelResolution.data?.channels.filter(isSendableChannel) ?? [],
    [channelResolution.data]
  );

  // Null outside an impersonation tab: there every path below is the
  // unchanged iframe/overview flow.
  const impersonationHandover: ImpersonationHandover | null =
    isImpersonationTab()
      ? // An impersonation session carries no integrator, so the API refuses
        // a host handover for it (409 no_handover_target): a cloud session
        // is saved and activated only, never handed over.
        !isCloudSession && availableChannels.length > 0
        ? 'marketplace'
        : 'none'
      : null;

  /**
   * Channels a click would enqueue.
   *
   * One channel, or one named on the iframe URL, needs no choice at all. With
   * several the seller picks, and the URL channel is the preselection rather
   * than a bypass — otherwise a marketplace seller with a Shopify store would
   * never see the selection at all, because the marketplace names its
   * channel in every embed.
   */
  const targetChannels = useMemo(() => {
    const open = availableChannels.filter(
      (channel) => !publishedChannelIds.includes(channel.id)
    );
    if (open.length === 0) return [];
    if (availableChannels.length === 1) return open;
    return open.filter((channel) => selectedChannelIds.includes(channel.id));
  }, [availableChannels, publishedChannelIds, selectedChannelIds]);

  // Preselection, once per resolution: the channel whose key the iframe URL
  // named, otherwise every channel. Runs once per channel list, so a seller
  // who deliberately clears the selection is not overruled by a re-render.
  const preselectedFor = useRef<typeof availableChannels | null>(null);
  useEffect(() => {
    if (preselectedFor.current === availableChannels) return;
    if (availableChannels.length < 2) return;
    preselectedFor.current = availableChannels;
    if (selectedChannelIds.length > 0) return;
    const named = availableChannels.filter(
      (channel) => channel.key === iframeChannelName
    );
    setChannels(
      (named.length > 0 ? named : availableChannels).map((c) => c.id)
    );
  }, [
    availableChannels,
    iframeChannelName,
    selectedChannelIds.length,
    setChannels,
  ]);

  const hasTitle = title.trim().length > 0;

  // Embroidery motifs must carry a confirmed thread mapping before they can
  // ship. Without this the whole thread ceiling is advisory: dismissing the
  // mapping dialog leaves the raw motif in place, and it publishes with as many
  // colours as it happens to have.
  //
  // Read from the saved position state, never from the live canvas. By the time
  // the seller reaches the Publish step, MainLayout has swapped the editor out
  // for PublishLayout and CanvasEditor's unmount has already set the store's
  // canvas to null — a check against it would find nothing to complain about and
  // wave everything through. The server enforces the same rule on status change;
  // this one exists so the button explains itself before the request is made.
  //
  // The predicate looks for an embFileKey without a confirmedMapping, so designs
  // created before this feature — which carry neither — are unaffected.
  // Subscribed rather than read through getState(): the value has to recompute
  // when a position changes, and masterPositions is what changes.
  const masterPositions = useCanvasStateManager(
    (state) => state.masterPositions
  );

  const unconfirmedEmbroideryMotifs = useMemo(() => {
    return Object.values(masterPositions).reduce((count, position) => {
      const objects = position?.json?.objects;
      if (!Array.isArray(objects)) return count;
      return (
        count +
        objects.filter((obj) => {
          const candidate = obj as {
            embFileKey?: unknown;
            confirmedMapping?: unknown;
          };
          if (typeof candidate.embFileKey !== 'string') return false;
          const mapping = candidate.confirmedMapping;
          return (
            !mapping ||
            typeof mapping !== 'object' ||
            Object.keys(mapping).length === 0
          );
        }).length
      );
    }, 0);
  }, [masterPositions]);

  // A product or a saved position that is no longer set up for printing
  // keeps the listing from going live (R13, R31). The server reported the
  // reasons with the design; deselecting the product or removing the motif
  // from the position clears them here, the server checks again on publish.
  const calibrationBlockers = useDesignStore(
    (state) => state.calibrationBlockers
  );
  const calibrationBlocker = useMemo(() => {
    const activeProductIds = new Set(
      Object.entries(selections)
        .filter(([, selection]) => selection.isActive)
        .map(([productId]) => productId)
    );
    // Same rule as the server: a position counts only with a placement.
    const positionsWithDesign = positionsWithPlacement(masterPositions);
    return activeCalibrationBlocker(
      calibrationBlockers,
      activeProductIds,
      positionsWithDesign
    );
  }, [calibrationBlockers, selections, masterPositions]);

  // While the AI suggestion is still running the fields are about to change;
  // publishing now would ship the old title and let the late answer rewrite
  // the store after the fact.
  const isAiGenerating = aiGenerationStatus === 'loading';

  // With several channels the seller has to pick one; with none the click
  // still finishes the listing, it just sends nothing.
  const needsChannelChoice =
    !isCloudSession &&
    availableChannels.length > 1 &&
    availableChannels.some(
      (channel) => !publishedChannelIds.includes(channel.id)
    ) &&
    targetChannels.length === 0;

  // Until the server has said which destinations exist, a click would flip
  // the design to ACTIVE and send it nowhere — not even to the cloud.
  const channelsUnknown = !channelResolution.data;

  const canPublish =
    hasTitle &&
    totalVariants > 0 &&
    unpricedProductNames.length === 0 &&
    unconfirmedEmbroideryMotifs === 0 &&
    calibrationBlocker === null &&
    !isAiGenerating &&
    !needsChannelChoice &&
    !channelsUnknown &&
    !publishRunning;

  const publish = async () => {
    setIsPublishing(true);
    // A suggestion still in flight must not land after the design is published.
    invalidateAiGeneration();
    try {
      setDesignName(title);
      setDesignDescription(description);

      // The step change starts saves without waiting for them, and a call
      // made during one queues a trailing pass. Both have to land before the
      // status flips: a pass sends the store's status, and one that finished
      // after the ACTIVE call would write DRAFT back.
      if ((await awaitPendingSaves()) === 'timeout') {
        toast.error(i18n.t('creator.header.saveFailed'));
        return;
      }

      // Then save once more so the latest title, description and canvas
      // state are what gets published. `saveDesign` reports failure as null.
      const saved = await saveDesign();
      if (!saved) {
        toast.error(i18n.t('creator.header.saveFailed'));
        return;
      }
      const targetId = saved.id;

      // Adopt, not set: `setStatus` would mark the design dirty and trip the
      // leave warning during the cloud handover. Restored if the PATCH fails,
      // otherwise the next save would send ACTIVE and publish on the side.
      const previousStatus = useDesignStore.getState().status;
      adoptStatus('ACTIVE');
      try {
        await updateDesign(targetId, {
          status: 'ACTIVE',
          tags,
          publishCategory: category || undefined,
          publishCollection: collection || undefined,
          // Only a cloud session owns this field. It used to receive the
          // publish store's placeholder default, which wrote "shopify" onto
          // every design published outside an iframe.
          channelName: isCloudSession ? handoverChannelName : undefined,
        });
      } catch (error) {
        adoptStatus(previousStatus);
        throw error;
      }

      await queryClient.invalidateQueries({ queryKey: designKeys.lists() });

      // Polaris embed only: the Shopify admin app learns about the published
      // design here (lib/embed-messages.ts); a no-op for every other embedder.
      postEmbedMessage({
        type: 'designdeck:embed:published',
        designId: targetId,
      });

      // The lock holds through the handover: the marketplace publish waits
      // server-side for mockups, and a save during that window could still
      // delete colour rows the mockups belong to.
      if (impersonationHandover === 'marketplace') {
        // No parent shop window to notify in a top-level tab.
        await publishToChannels(
          targetId,
          targetChannels.map((channel) => channel.id)
        );
        toast.success(i18n.t('creator.publish.impersonationHandedOver'));
        navigate('/designs', { replace: true });
      } else if (impersonationHandover === 'none') {
        toast.success(i18n.t('creator.publish.impersonationSavedNotSent'));
        navigate('/designs', { replace: true });
      } else if (isCloudSession) {
        // Unchanged: the cloud takes the listing over its own callback and
        // redirects the shop page, so there is nothing to enqueue here.
        const redirectUrl = await requestCloudCallback(
          targetId,
          handoverChannelName
        );
        // The cloud callback freezes before it calls the cloud, so the
        // unpublished-changes hint is settled by now.
        await refreshDesign(targetId);
        // Never in the Polaris embed: navigating the Shopify admin's page away
        // would leave the app; the host reacts to the published message.
        if (redirectUrl && !isPolarisEmbed()) {
          window.parent.location.href = redirectUrl;
        } else {
          navigate('/designs', { replace: true });
        }
      } else if (targetChannels.length > 0) {
        const result = await publishToChannels(
          targetId,
          targetChannels.map((channel) => channel.id)
        );
        if (result?.enqueued === 0) {
          toast.success(i18n.t('creator.publish.nothingToSend'));
        }
        navigate('/designs', { replace: true });
      } else {
        // No channel at all: the listing is finished, it just has nowhere to
        // go yet.
        toast.success(i18n.t('creator.publish.noChannelSaved'));
        navigate('/designs', { replace: true });
      }
    } catch (error) {
      console.error('Publish failed:', error);
      toast.error(describeApiError(error));
    } finally {
      setIsPublishing(false);
    }
  };

  // Named so the blocked step can say what to fix instead of just greying out.
  const publishBlockedReason = publishRunning
    ? i18n.t('creator.publish.blockedPublishRunning')
    : unpricedProductNames.length > 0
      ? i18n.t('creator.publish.blockedUnpricedProducts', {
          names: listProductNames(unpricedProductNames),
        })
      : calibrationBlocker
        ? calibrationBlocker.code === 'product_uncalibrated'
          ? i18n.t('creator.publish.blockedProductUncalibrated', {
              productName: calibrationBlocker.productName,
            })
          : i18n.t('creator.publish.blockedPositionUncalibrated', {
              productName: calibrationBlocker.productName,
              positionName: calibrationBlocker.positionName,
            })
        : unconfirmedEmbroideryMotifs > 0
          ? i18n.t('creator.publish.blockedThreadsUnconfirmed')
          : isAiGenerating
            ? i18n.t('creator.publish.blockedAiGenerating')
            : needsChannelChoice
              ? i18n.t('creator.publish.blockedNoChannelSelected')
              : channelsUnknown
                ? i18n.t(
                    channelResolution.isError
                      ? 'creator.publish.blockedChannelsFailed'
                      : 'creator.publish.blockedChannelsLoading'
                  )
                : null;

  return {
    availableChannels,
    targetChannels,
    publishedChannelIds,
    isCloudSession,
    publish,
    isPublishing,
    canPublish,
    publishBlockedReason,
    impersonationHandover,
  };
}
